import { useEffect, useRef } from 'react';
import type { FormikValues } from 'formik';
import { useFormikContext } from 'formik';
import { isEqual, pick } from 'lodash';

type UseFormikChangeListenerProps<Values> = {
  onChange: (values: Values, previousValues: Values) => void;
  listenForKeys?: string | string[];
  runOnFirstRender?: boolean;
};

const useFormikChangeListener = <Values = FormikValues>({
  onChange,
  listenForKeys,
  runOnFirstRender = false,
}: UseFormikChangeListenerProps<Values>): void => {
  const { values } = useFormikContext<Values>();
  const previousValuesRef = useRef(values);
  const didRunRef = useRef(false);
  // Avoid endless render loops by keeping these in a stable ref - changes in these props should never
  // re-trigger the change listener effect.
  const stablePropsRef = useRef({ onChange, listenForKeys });

  // This effect must always precede the change listener effect, so that that effect always references
  // the most updated props.
  useEffect(() => {
    stablePropsRef.current.onChange = onChange;
    stablePropsRef.current.listenForKeys = listenForKeys;
  }, [onChange, listenForKeys]);

  // Change listener effect
  useEffect(() => {
    const previousValues = previousValuesRef.current;
    const listenForKeysProp = stablePropsRef.current.listenForKeys;
    const isChanged = listenForKeysProp
      ? !isEqual(pick(values, listenForKeysProp), pick(previousValues, listenForKeysProp))
      : !isEqual(values, previousValues);
    if (isChanged || (runOnFirstRender && !didRunRef.current)) {
      stablePropsRef.current.onChange(values, previousValues);
      didRunRef.current = true;
    }
    previousValuesRef.current = values;
  }, [values, runOnFirstRender]);
};

export default useFormikChangeListener;
